import { intlDefaultFormatter, type IntlWithFormatter } from '../../../../contexts/IntlContext';
import { NumericField } from '../../../../fields/NumericField';
import { SelectorField } from '../../../../fields/SelectorField';
import { SecurityTypesUtils, type Currency, type CustomerMarketAccount } from '../../../../types';
import type { InitialReferenceDataParams } from '../OMSReferenceDataSlice/types';
import { INITIAL_REFERENCEDATA_STATE, initialSliceDependencies } from '../OrderSlice/utils';
import type { SwapState } from './types';
import { getAvailableItemsForCurrencies } from './utils';

export const getSwapInitialState = (
  initialParams?: InitialReferenceDataParams,
  intl: IntlWithFormatter = intlDefaultFormatter
): SwapState => {
  const initialMarketAccountsReferenceData =
    initialParams?.marketAccounts ?? INITIAL_REFERENCEDATA_STATE.marketAccounts;
  const initialSecuritiesReferenceData = initialParams?.securities ?? INITIAL_REFERENCEDATA_STATE.securities;
  const initialCurrenciesReferenceData = initialParams?.currencies ?? INITIAL_REFERENCEDATA_STATE.currencies;

  const availableCurrencies = getAvailableItemsForCurrencies(
    initialCurrenciesReferenceData.currenciesList,
    initialSecuritiesReferenceData.securitiesList
  );

  let fromCurrencyInitialValue: Currency | undefined;
  let toCurrencyInitialValue: Currency | undefined;
  if (initialParams?.symbol) {
    const security = initialSecuritiesReferenceData.securitiesBySymbol.get(initialParams.symbol);
    if (SecurityTypesUtils.Spot.isType(security)) {
      fromCurrencyInitialValue = initialCurrenciesReferenceData.currenciesBySymbol.get(security.QuoteCurrency);
      toCurrencyInitialValue = initialCurrenciesReferenceData.currenciesBySymbol.get(security.BaseCurrency);
    }
  }

  return {
    intl,
    form: {
      fromCurrency: new SelectorField<Currency>({
        availableItems: availableCurrencies,
        value: fromCurrencyInitialValue,
        idProperty: 'Symbol',
      }),
      fromQty: new NumericField({
        name: 'Amount',
      }),
      toCurrency: new SelectorField<Currency>({
        availableItems: availableCurrencies,
        value: toCurrencyInitialValue,
        idProperty: 'Symbol',
      }),
      toQty: new NumericField({
        name: 'Amount',
      }),
      marketAccount: new SelectorField<CustomerMarketAccount>({
        availableItems: initialMarketAccountsReferenceData.customerMarketAccountsList,
        value: initialMarketAccountsReferenceData.customerMarketAccountsList.at(-1),
        idProperty: 'MarketAccountID',
      }),
    },
    meta: {
      lastTouched: 'fromQty',
      step: 'idle',
      sentOrderPayload: undefined,
      order: undefined,
      quoteCountdown: undefined,
      transactionID: undefined,
      balanceTransaction: undefined,
      takingLongerThanExpected: false,
    },
    requestStep: 'resting',
    mode: 'swap',
    quote: undefined,
    quoteRequest: undefined,
    dependencies: {
      ...initialSliceDependencies,
    },
    referenceData: {
      ...INITIAL_REFERENCEDATA_STATE,
      currencies: initialCurrenciesReferenceData,
      marketAccounts: initialMarketAccountsReferenceData,
      securities: initialSecuritiesReferenceData,
      favoredPairCurrency: initialParams?.favoredPairCurrency,
      homeCurrency: initialParams?.homeCurrency,
    },
  };
};
